/**
 * 대시보드 컴포넌트 그룹 관리 유틸리티
 * 파워포인트 스타일의 그룹핑 기능 지원
 */

/**
 * 새로운 그룹 ID 생성
 * @returns {string} 고유한 그룹 ID
 */
export const generateGroupId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `group_${timestamp}_${random}`;
};

/**
 * 템플릿에서 그룹 정보 가져오기
 * @param {Object} template - 대시보드 템플릿
 * @returns {Array} 그룹 배열 (없으면 빈 배열)
 */
export const getGroups = (template) => {
  if (!template) return [];
  return template.groups || [];
};

/**
 * 특정 그룹 정보 가져오기
 * @param {Object} template - 대시보드 템플릿
 * @param {string} groupId - 그룹 ID
 * @returns {Object|null} 그룹 객체 또는 null
 */
export const getGroup = (template, groupId) => {
  const groups = getGroups(template);
  return groups.find(g => g.id === groupId) || null;
};

/**
 * 컴포넌트가 속한 그룹 ID 가져오기
 * @param {Object} component - 컴포넌트 객체
 * @returns {string|null} 그룹 ID 또는 null
 */
export const getComponentGroupId = (component) => {
  return component?.group_id || null;
};

/**
 * 특정 그룹에 속한 모든 컴포넌트 ID 가져오기
 * @param {Object} template - 대시보드 템플릿
 * @param {string} groupId - 그룹 ID
 * @returns {Array<string>} 컴포넌트 ID 배열
 */
export const getGroupComponentIds = (template, groupId) => {
  if (!template || !template.components) return [];
  return template.components
    .filter(comp => comp.group_id === groupId)
    .map(comp => comp.id);
};

/**
 * 컴포넌트들을 그룹으로 묶기
 * @param {Object} template - 대시보드 템플릿
 * @param {Array<string>} componentIds - 그룹화할 컴포넌트 ID 배열
 * @param {Object} options - 그룹 옵션 { name, locked, common_styles }
 * @returns {Object} 업데이트된 템플릿
 */
export const createGroup = (template, componentIds, options = {}, currentLayout = null) => {
  if (!template || !componentIds || componentIds.length === 0) {
    return template;
  }

  const groupId = generateGroupId();
  
  // 원본 크기/위치 저장 (리사이징 시 참조용)
  let originalLayout = null;
  if (currentLayout) {
    originalLayout = currentLayout
      .filter(item => componentIds.includes(item.i))
      .map(item => ({
        id: item.i,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h
      }));
  }
  
  const newGroup = {
    id: groupId,
    name: options.name || `그룹 ${(getGroups(template).length + 1)}`,
    locked: options.locked || false,
    isolatedSelection: options.isolatedSelection || false, // 런타임 모드 독립선택 기능
    common_styles: options.common_styles || {},
    original_layout: originalLayout, // 원본 레이아웃 저장
    created_at: new Date().toISOString()
  };

  // 템플릿 복사
  const updatedTemplate = { ...template };
  
  // groups 배열 초기화 (없으면 생성)
  if (!updatedTemplate.groups) {
    updatedTemplate.groups = [];
  }
  
  // 새 그룹 추가
  updatedTemplate.groups = [...updatedTemplate.groups, newGroup];
  
  // 컴포넌트에 group_id 할당
  updatedTemplate.components = updatedTemplate.components.map(comp => {
    if (componentIds.includes(comp.id)) {
      return { ...comp, group_id: groupId };
    }
    return comp;
  });

  return updatedTemplate;
};

/**
 * 그룹 해제
 * @param {Object} template - 대시보드 템플릿
 * @param {string} groupId - 해제할 그룹 ID
 * @returns {Object} 업데이트된 템플릿
 */
export const ungroupComponents = (template, groupId) => {
  if (!template || !groupId) return template;

  const updatedTemplate = { ...template };
  
  // 그룹 제거
  if (updatedTemplate.groups) {
    updatedTemplate.groups = updatedTemplate.groups.filter(g => g.id !== groupId);
  }
  
  // 컴포넌트에서 group_id 제거
  updatedTemplate.components = updatedTemplate.components.map(comp => {
    if (comp.group_id === groupId) {
      const { group_id, ...rest } = comp;
      return rest;
    }
    return comp;
  });

  return updatedTemplate;
};

/**
 * 그룹 정보 업데이트
 * @param {Object} template - 대시보드 템플릿
 * @param {string} groupId - 업데이트할 그룹 ID
 * @param {Object} updates - 업데이트할 필드들
 * @returns {Object} 업데이트된 템플릿
 */
export const updateGroup = (template, groupId, updates) => {
  if (!template || !groupId) return template;

  const updatedTemplate = { ...template };
  
  if (updatedTemplate.groups) {
    updatedTemplate.groups = updatedTemplate.groups.map(g => {
      if (g.id === groupId) {
        return { ...g, ...updates };
      }
      return g;
    });
  }

  return updatedTemplate;
};

/**
 * 그룹 잠금/해제
 * @param {Object} template - 대시보드 템플릿
 * @param {string} groupId - 그룹 ID
 * @param {boolean} locked - 잠금 여부
 * @returns {Object} 업데이트된 템플릿
 */
export const setGroupLocked = (template, groupId, locked) => {
  return updateGroup(template, groupId, { locked });
};

/**
 * 컴포넌트가 잠긴 그룹에 속하는지 확인
 * @param {Object} template - 대시보드 템플릿
 * @param {string} componentId - 컴포넌트 ID
 * @returns {boolean} 잠긴 그룹에 속하면 true
 */
export const isComponentInLockedGroup = (template, componentId) => {
  if (!template || !componentId) return false;
  
  const component = template.components?.find(c => c.id === componentId);
  if (!component || !component.group_id) return false;
  
  const group = getGroup(template, component.group_id);
  return group?.locked === true;
};

/**
 * 선택된 컴포넌트들이 모두 같은 그룹에 속하는지 확인
 * @param {Object} template - 대시보드 템플릿
 * @param {Array<string>} componentIds - 컴포넌트 ID 배열
 * @returns {boolean} 모두 같은 그룹에 속하면 true
 */
export const areComponentsInSameGroup = (template, componentIds) => {
  if (!template || !componentIds || componentIds.length === 0) return false;
  
  const groupIds = componentIds.map(id => {
    const comp = template.components?.find(c => c.id === id);
    return comp?.group_id;
  }).filter(Boolean);
  
  if (groupIds.length === 0) return false;
  
  const firstGroupId = groupIds[0];
  return groupIds.every(gid => gid === firstGroupId);
};

/**
 * 그룹의 바운딩 박스 계산 (레이아웃 기준)
 * @param {Object} template - 대시보드 템플릿
 * @param {Array} layout - 현재 레이아웃 배열
 * @param {string} groupId - 그룹 ID
 * @returns {Object|null} { x, y, w, h, components } 또는 null
 */
export const getGroupBoundingBox = (template, layout, groupId) => {
  if (!template || !layout || !groupId) return null;
  
  const componentIds = getGroupComponentIds(template, groupId);
  if (componentIds.length === 0) return null;
  
  const layoutItems = layout.filter(item => componentIds.includes(item.i));
  if (layoutItems.length === 0) return null;
  
  let minX = Infinity, minY = Infinity;
  let maxX = -Infinity, maxY = -Infinity;
  
  layoutItems.forEach(item => {
    minX = Math.min(minX, item.x);
    minY = Math.min(minY, item.y);
    maxX = Math.max(maxX, item.x + item.w);
    maxY = Math.max(maxY, item.y + item.h);
  });
  
  return {
    x: minX,
    y: minY,
    w: maxX - minX,
    h: maxY - minY,
    components: layoutItems
  };
};

/**
 * 그룹 리사이즈 (비율 유지)
 * @param {Object} template - 대시보드 템플릿
 * @param {Array} layout - 현재 레이아웃 배열
 * @param {string} groupId - 그룹 ID
 * @param {Object} newBounds - 새로운 바운딩 박스 { x, y, w, h }
 * @returns {Array} 업데이트된 레이아웃 배열
 */
export const resizeGroup = (template, layout, groupId, newBounds) => {
  const currentBoundingBox = getGroupBoundingBox(template, layout, groupId);
  if (!currentBoundingBox) return layout;
  
  // 그룹 정보 가져오기
  const group = template.groups?.find(g => g.id === groupId);
  
  // 원본 레이아웃이 있으면 사용, 없으면 현재 레이아웃 기준
  const useOriginal = group?.original_layout && group.original_layout.length > 0;
  
  // 원본 바운딩 박스 계산
  let originalBoundingBox;
  if (useOriginal) {
    // 원본 레이아웃으로 바운딩 박스 계산
    const originalItems = group.original_layout;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    originalItems.forEach(item => {
      minX = Math.min(minX, item.x);
      minY = Math.min(minY, item.y);
      maxX = Math.max(maxX, item.x + item.w);
      maxY = Math.max(maxY, item.y + item.h);
    });
    originalBoundingBox = {
      x: minX,
      y: minY,
      w: maxX - minX,
      h: maxY - minY
    };
  } else {
    originalBoundingBox = currentBoundingBox;
  }
  
  const scaleX = newBounds.w / originalBoundingBox.w;
  const scaleY = newBounds.h / originalBoundingBox.h;
  
  const componentIds = getGroupComponentIds(template, groupId);
  
  // 원본 또는 현재 레이아웃으로 행 그룹화
  const sourceItems = useOriginal && group.original_layout 
    ? group.original_layout 
    : layout.filter(item => componentIds.includes(item.i));
  
  // 그룹 컴포넌트들을 정렬 (y, x 순서)
  const groupItems = sourceItems
    .map(item => {
      // original_layout 형식을 layout 형식으로 변환
      if (item.id) {
        return { i: item.id, ...item };
      }
      return item;
    })
    .sort((a, b) => {
      if (a.y !== b.y) return a.y - b.y;
      return a.x - b.x;
    });
  
  // 행별로 그룹화
  const rowGroups = [];
  let currentRow = null;
  let currentRowItems = [];
  
  groupItems.forEach(item => {
    if (currentRow === null || item.y !== currentRow) {
      if (currentRowItems.length > 0) {
        rowGroups.push({ y: currentRow, items: currentRowItems });
      }
      currentRow = item.y;
      currentRowItems = [item];
    } else {
      currentRowItems.push(item);
    }
  });
  if (currentRowItems.length > 0) {
    rowGroups.push({ y: currentRow, items: currentRowItems });
  }
  
  // 각 컴포넌트의 새 위치/크기 계산
  const updatedPositions = new Map();
  
  rowGroups.forEach(rowGroup => {
    const { y: rowY, items: rowItems } = rowGroup;
    
    // 이 행의 새 y 위치 계산 (원본 기준)
    const relYRatio = (rowY - originalBoundingBox.y) / originalBoundingBox.h;
    const exactNewY = newBounds.y + relYRatio * newBounds.h;
    const newY = Math.round(exactNewY);
    
    // 각 컴포넌트를 원본 위치 비율로 배치 (간격 유지)
    rowItems.forEach((item, indexInRow) => {
      // 원본 레이아웃에서 이 컴포넌트 정보 가져오기
      let originalItem = item;
      if (useOriginal && group.original_layout) {
        const found = group.original_layout.find(o => o.id === item.i);
        if (found) {
          originalItem = found;
        }
      }
      
      // 원본 기준으로 x 위치 비율 계산 (원본 바운딩 박스 기준)
      const relXRatio = (originalItem.x - originalBoundingBox.x) / originalBoundingBox.w;
      const exactNewX = newBounds.x + relXRatio * newBounds.w;
      const newX = Math.round(exactNewX);
      
      // 원본 기준으로 크기 비율 계산
      const relWRatio = originalItem.w / originalBoundingBox.w;
      const relHRatio = originalItem.h / originalBoundingBox.h;
      const exactNewW = relWRatio * newBounds.w;
      const exactNewH = relHRatio * newBounds.h;
      const newW = Math.max(1, Math.round(exactNewW));
      const newH = Math.max(1, Math.round(exactNewH));
      
      updatedPositions.set(item.i, {
        x: newX,
        y: newY,
        w: newW,
        h: newH
      });
    });
  });
  
  // 전체 레이아웃에 적용
  return layout.map(item => {
    if (!componentIds.includes(item.i)) return item;
    
    const newPos = updatedPositions.get(item.i);
    return {
      ...item,
      ...newPos
    };
  });
};

/**
 * 그룹이 비어있는지 확인
 * @param {Object} template - 대시보드 템플릿
 * @param {string} groupId - 그룹 ID
 * @returns {boolean} 비어있으면 true
 */
export const isGroupEmpty = (template, groupId) => {
  const componentIds = getGroupComponentIds(template, groupId);
  return componentIds.length === 0;
};

/**
 * 빈 그룹들 정리 (컴포넌트가 없는 그룹 제거)
 * @param {Object} template - 대시보드 템플릿
 * @returns {Object} 정리된 템플릿
 */
export const cleanupEmptyGroups = (template) => {
  if (!template || !template.groups) return template;
  
  const updatedTemplate = { ...template };
  updatedTemplate.groups = updatedTemplate.groups.filter(group => 
    !isGroupEmpty(template, group.id)
  );
  
  return updatedTemplate;
};

